import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  AlarmClock,
  AtSign,
  Ban,
  Bell,
  Building2,
  CalendarClock,
  CheckCheck,
  CircleCheck,
  Clock3,
  FolderKanban,
  GitPullRequest,
  LifeBuoy,
  RefreshCw,
  Undo2,
  UserPlus,
  BookOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UserAvatar, type AvatarPerson } from '@/components/composites/user-avatar'
import { LinkedText } from '@/components/composites/linked-text'
import { cn } from '@/lib/utils'
import { useNotifications, type AppNotification } from './useNotifications'

/**
 * What happened, in the product itself.
 *
 * This exists because everything the app had to say went to Slack, and Slack can be switched
 * off by settings nobody in the room controls — a missing OAuth scope was enough to make the
 * whole notification system look like it had never been built. A bell has no such dependency.
 *
 * Read state is the point of the thing, so it is honest about it: opening the panel does NOT
 * mark everything read. Somebody who glances at a badge, sees four, and comes back later to
 * find zero has lost four things. They are marked when the person acts — opening one, or
 * saying so explicitly.
 */
const ICON: Record<string, typeof Bell> = {
  mention: AtSign,
  approval_requested: CheckCheck,
  assigned: UserPlus,
  approved: CircleCheck,
  sent_back: Undo2,
  completed: CircleCheck,
  blocked: Ban,
  new_client: Building2,
  new_project: FolderKanban,
  new_request: GitPullRequest,
  new_work: FolderKanban,
  status_changed: RefreshCw,
  eta_changed: CalendarClock,
  overdue_no_eta: AlarmClock,
  overdue_escalated: AlarmClock,
  support_assigned: LifeBuoy,
  support_team_assigned: LifeBuoy,
  diary_plan: BookOpen,
  diary_review: BookOpen,
}

function iconFor(kind: string): typeof Bell {
  return kind.startsWith('due_in_') ? Clock3 : (ICON[kind] ?? Bell)
}

/** Relative, because "2h ago" is what somebody wants from a notification list. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString()
}

export function NotificationBell({
  workspaceSlug,
  people = {},
}: {
  workspaceSlug: string
  /**
   * Faces for the people who caused things, keyed by member id.
   *
   * Optional, and the bell is correct without it: every title already names the actor
   * ("Rahul needs your approval on…"), so the avatar is recognition rather than information.
   * The shell does not fetch the member list just to draw one.
   */
  people?: Record<string, AvatarPerson>
}) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'all' | 'work' | 'support' | 'diary'>('all')
  const navigate = useNavigate()
  const { items, unread, isPending, markRead } = useNotifications(workspaceSlug)
  const visibleItems = items.filter((n) => {
    if (tab === 'all') return true
    if (tab === 'diary') return n.kind.startsWith('diary_')
    const isSupport = n.kind === 'support_assigned' || n.kind === 'support_team_assigned' || Boolean(n.href?.startsWith('/support-tickets')) || Boolean(n.href?.startsWith('/support-ticket'))
    if (tab === 'support') return isSupport
    return !isSupport && !n.kind.startsWith('diary_') && (Boolean(n.cardId) || Boolean(n.href?.startsWith('/work')) || ['assigned', 'new_work', 'approval_requested', 'approved', 'sent_back', 'completed', 'blocked', 'status_changed'].includes(n.kind) || n.kind.startsWith('due_in_'))
  })

  function openItem(n: AppNotification) {
    if (!n.readAt) markRead.mutate([n.id])
    setOpen(false)
    if (n.href) navigate(`/w/${workspaceSlug}${n.href}`)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-9"
          aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications'}
        >
          <Bell className="size-4" />
          {unread > 0 ? (
            // A count, not a dot: "something happened" and "eleven things happened" are
            // different decisions about whether to look now.
            <span
              className={cn(
                'num absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full',
                'bg-danger px-1 text-micro font-semibold text-text-inverse',
              )}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0 max-md:w-[calc(100vw-1rem)]">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-xs font-semibold">Notifications</p>
          {unread > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-tiny"
              onClick={() => markRead.mutate([])}
              disabled={markRead.isPending}
            >
              Mark all read
            </Button>
          ) : null}
        </div>

        <div className="grid grid-cols-3 border-b border-border-soft p-1">
          {(['all', 'work', 'support', 'diary'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn('rounded-md px-2 py-1.5 text-tiny font-medium capitalize', tab === value ? 'bg-brand-soft text-brand-dark' : 'text-text-muted hover:bg-muted')}
            >
              {value === 'all' ? 'All' : value === 'work' ? 'My Work' : value === 'support' ? 'My Support' : 'Diary'}
            </button>
          ))}
        </div>
        <div className="max-h-96 overflow-y-auto overscroll-contain">
          {isPending ? (
            <p className="px-3 py-8 text-center text-tiny text-text-muted">Loading…</p>
          ) : visibleItems.length === 0 ? (
            <p className="px-3 py-8 text-center text-tiny text-text-muted">
              {tab === 'support' ? 'No Support notifications assigned to you yet.' : tab === 'work' ? 'No Work notifications assigned to you yet.' : 'Nothing yet. New visible work, approvals, Support assignments, status changes, mentions and deadline reminders will appear here.'}
            </p>
          ) : (
            <ul className="divide-y divide-border-soft">
              {visibleItems.map((n) => {
                const Icon = iconFor(n.kind)
                const actor = n.actorId ? people[n.actorId] : undefined
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      className={cn(
                        'flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors dur-fast',
                        'hover:bg-brand-softer',
                        // Unread is a tint plus a marker, never colour alone.
                        !n.readAt && 'bg-brand-soft/40',
                      )}
                    >
                      <span className="relative mt-0.5 shrink-0">
                        {n.kind.startsWith('diary_') ? (
                          <span className="grid size-6 place-items-center rounded-full bg-brand-accent-soft" title="Daily diary">
                            <BookOpen className="size-3.5 text-brand-accent" aria-hidden />
                          </span>
                        ) : actor ? (
                          <UserAvatar person={actor} size="sm" />
                        ) : (
                          <span className="grid size-6 place-items-center rounded-full bg-muted">
                            <Icon className="size-3 text-text-secondary" />
                          </span>
                        )}
                        {!n.readAt ? (
                          <span
                            aria-label="Unread"
                            className="absolute -top-0.5 -left-0.5 size-2 rounded-full bg-brand ring-2 ring-card"
                          />
                        ) : null}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-start gap-1.5">
                          <Icon className="mt-0.5 size-3 shrink-0 text-text-muted" aria-hidden />
                          <span className="text-sm leading-snug">{n.title}</span>
                        </span>
                        {n.body ? (
                          // The body can hold a comment excerpt, so URLs in it are links.
                          <LinkedText
                            text={n.body}
                            className={cn('mt-0.5 block text-tiny text-text-secondary', n.kind.startsWith('diary_') ? 'line-clamp-4 whitespace-pre-line' : 'line-clamp-2')}
                          />
                        ) : null}
                        <span className="mt-0.5 block text-tiny text-text-muted">
                          {ago(n.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
